import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { resolveHeaderCoords } from './headerLocationService';
import { fetchHeaderWeather, getLastCachedHeaderWeather, type HeaderWeatherDetail } from './weatherService';

interface SmartHeaderState {
  weather: HeaderWeatherDetail | null;
  scrolled: boolean;
  /** True when live weather data is available (false shows -- placeholders). */
  weatherAvailable: boolean;
}

const SmartHeaderContext = createContext<SmartHeaderState | null>(null);

const REFRESH_MS = 30 * 60 * 1000;
const LOG_PREFIX = '[RideMaadi Header Weather]';

/** Shared header data - fetched once, cached 30 min, not refetched on route changes. */
export function SmartHeaderProvider({
  children,
  scrollRootId = 'app-scroll-region',
}: {
  children: ReactNode;
  scrollRootId?: string;
}) {
  const [weather, setWeather] = useState<HeaderWeatherDetail | null>(null);
  const [weatherAvailable, setWeatherAvailable] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const fetchedRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const load = async () => {
      const coords = await resolveHeaderCoords();
      if (coords) {
        const wx = await fetchHeaderWeather(coords.lat, coords.lon);
        if (wx) {
          setWeather(wx);
          setWeatherAvailable(true);
          return;
        }
      }

      const cached = getLastCachedHeaderWeather();
      if (cached) {
        setWeather(cached);
        setWeatherAvailable(true);
        console.warn(`${LOG_PREFIX} Using cached weather - GPS or Open-Meteo unavailable`);
        return;
      }

      console.warn(
        `${LOG_PREFIX} Weather unavailable${coords ? '' : ' (geolocation denied or unavailable)'}`,
      );
      setWeatherAvailable(false);
    };

    void load();

    refreshTimerRef.current = setInterval(() => {
      void resolveHeaderCoords().then(async (coords) => {
        if (coords) {
          const wx = await fetchHeaderWeather(coords.lat, coords.lon);
          if (wx) {
            setWeather(wx);
            setWeatherAvailable(true);
            return;
          }
        }
        const cached = getLastCachedHeaderWeather();
        if (cached) {
          setWeather(cached);
          setWeatherAvailable(true);
        } else {
          console.warn(`${LOG_PREFIX} Open-Meteo refresh failed`);
          setWeatherAvailable(false);
        }
      });
    }, REFRESH_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const root = scrollRootId ? document.getElementById(scrollRootId) : null;

    const onScroll = () => {
      const top = root ? root.scrollTop : window.scrollY;
      setScrolled(top > 4);
    };

    onScroll();
    const target: EventTarget = root ?? window;
    target.addEventListener('scroll', onScroll as EventListener, { passive: true });
    return () => target.removeEventListener('scroll', onScroll as EventListener);
  }, [scrollRootId]);

  const value = useMemo<SmartHeaderState>(
    () => ({
      weather,
      scrolled,
      weatherAvailable,
    }),
    [weather, scrolled, weatherAvailable],
  );

  return <SmartHeaderContext.Provider value={value}>{children}</SmartHeaderContext.Provider>;
}

export function useSmartHeader(): SmartHeaderState {
  const ctx = useContext(SmartHeaderContext);
  if (!ctx) {
    return {
      weather: null,
      scrolled: false,
      weatherAvailable: false,
    };
  }
  return ctx;
}
